import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { llmProvidersApi } from '../api/llmProviders';

export function useLLMProviders() {
  const queryClient = useQueryClient();

  // Query for fetching all providers
  const {
    data: providers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: llmProvidersApi.list,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Derive active provider from the list
  const activeProvider = providers.find((p) => p.is_active) || null;

  // Mutation for updating provider
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: { model?: string; temperature?: number } }) =>
      llmProvidersApi.update(id, updates),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] });
      queryClient.invalidateQueries({ queryKey: ['llm-providers', 'active'] });
    },
  });

  // Mutation for activating provider
  const activateMutation = useMutation({
    mutationFn: (id: number) => llmProvidersApi.activate(id),
    onSuccess: () => {
      // Invalidate and refetch all providers
      queryClient.invalidateQueries({ queryKey: ['llm-providers'] });
    },
  });

  return {
    providers,
    activeProvider,
    isLoading,
    error,
    updateProvider: updateMutation.mutate,
    activateProvider: activateMutation.mutate,
    isUpdating: updateMutation.isPending,
    isActivating: activateMutation.isPending,
  };
}
